import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';
import { useTracking } from 'react-tracking';
import { isLoginChallenge, login, LoginChallenge } from 'utils/apiUser';
import { setPasswordChallenge } from 'utils/setPassword';
import { delayMfaStorageKey, getWithExpiry } from 'utils/storage';
import { useAuth } from 'context/AuthContext/AuthContext';
import { useEnvironment } from 'context/EnvironmentContext/EnvironmentContext';
import { LocationState } from 'types';
export interface LoginFormValues {
  email: string;
  password: string;
}

interface LoginLocationState
  extends Omit<LocationState, 'email' | 'tokensUrl'> {
  passwordChanged?: boolean;
}

export const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Please enter your email'),
  password: Yup.string()
    .min(8, 'Minimum 8 characters required')
    .required('Please enter your password'),
});

/** Shared by `Login` and `LoginV2` so the challenge branching and the three
 * post-login redirects exist once. */
export default function useLoginForm() {
  const { setTokens } = useAuth();
  const { environment, setEnvironment } = useEnvironment();
  const location = useLocation<LoginLocationState>();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const history = useHistory();
  const { Track, trackEvent } = useTracking(
    { name: 'page_view', page: 'Login' },
    { dispatchOnMount: true }
  );

  const [passwordChanged, setPasswordChanged] = useState(false);

  useEffect(() => {
    if (location.pathname === '/pre-live') {
      setEnvironment('pre-live');
      history.replace('/');
    }
  }, [history, location, setEnvironment]);

  // Read the flag into state and strip it from the history entry, so the
  // message does not come back if the user reloads the login page. `from` has
  // to survive: it is where a successful login sends them.
  useEffect(() => {
    if (!location.state?.passwordChanged) {
      return;
    }
    setPasswordChanged(true);
    const { passwordChanged: _flag, ...rest } = location.state;
    history.replace(
      location.pathname,
      Object.keys(rest).length > 0 ? rest : undefined
    );
  }, [history, location]);

  function handleLoginChallenge(response: LoginChallenge, email: string) {
    if (response.challenge === 'SOFTWARE_TOKEN_MFA') {
      history.push('/verification', {
        tokensUrl: `${response.location}&code_verifier=${response.verifier}`,
        from: location.state?.from,
        email,
      });
    } else {
      setPasswordChallenge(response.verifier, email);
      history.push('/set-password');
    }
  }

  function handleNavigationAfterLogin(initiateMfa?: boolean): void {
    if (initiateMfa && !getWithExpiry(delayMfaStorageKey)) {
      history.push('/verification/initiate');
      return;
    }

    if (location.state?.from) {
      history.replace(location.state.from);
      return;
    }

    if (environment === 'pre-live') {
      history.replace('/getting-started');
    }
  }

  function handleSubmit(values: LoginFormValues): void {
    setError(null);
    setSubmitting(true);
    login(values, environment)
      .then((response) => {
        setSubmitting(false);
        if (isLoginChallenge(response)) {
          handleLoginChallenge(response, values.email);
        } else {
          trackEvent({ name: 'log_in', success: true, email: values.email });
          setTokens(response);
          handleNavigationAfterLogin(response.initiate_mfa);
        }
      })
      .catch((errorMessage) => {
        setSubmitting(false);
        setError(errorMessage);
        trackEvent({
          name: 'log_in',
          success: false,
          email: values.email,
          errorMessage,
        });
      });
  }

  return {
    initialValues: { email: '', password: '' } as LoginFormValues,
    validationSchema: LoginSchema,
    handleSubmit,
    error,
    isSubmitting,
    passwordChanged,
    Track,
  };
}
